import { FC, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Tag, InlineNotification } from '@carbon/react';
import { useAtom } from 'jotai';
import { PageHeader } from 'components/page-header';
import { formatNumber } from 'utils/number';
import routes from '~/routes';
import { evaluationOutputIdAtom, evaluationOutputAtom } from './atoms';
import type { BlobDetail, DevelopmentAreaDetail, PackageRef } from './types';
import styles from './page.module.scss';

const Row: FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <>
    <div className={styles.label}>{label}</div>
    <div className={styles.value}>{children}</div>
  </>
);

const DrainTag: FC<{ drained: boolean }> = ({ drained }) => (
  <Tag type={drained ? 'green' : 'gray'} size="sm">
    {drained ? 'Drained' : 'Pending'}
  </Tag>
);

const BlobPath: FC<{ path: string | null }> = ({ path }) => {
  if (path) return <code className={styles.blobPath}>{path}</code>;
  return <>-</>;
};

const DevelopmentAreaSection: FC<{ da: DevelopmentAreaDetail }> = ({ da }) => (
  <div className={styles.section}>
    <h4>Development Area</h4>
    <div className={styles.detailGrid}>
      <Row label="DA Evaluation ID">{da.evaluationId}</Row>
      <Row label="Name">{da.name ?? '-'}</Row>
      <Row label="Basin">{da.basin ?? '-'}</Row>
      <Row label="Tier">{da.tier ?? '-'}</Row>
      <Row label="Operator">{da.operator ?? '-'}</Row>
      {da.developmentAreaId && (
        <Row label="Development Area">
          <a
            className={styles.link}
            href={`/development_areas/${da.developmentAreaId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {da.name} (#{da.developmentAreaId})
          </a>
        </Row>
      )}
    </div>
  </div>
);

const BlobSection: FC<{ blob: BlobDetail | null }> = ({ blob }) => (
  <div className={styles.section}>
    <h4>Blob Drain State</h4>
    {!blob ? (
      <div className={styles.detailGrid}>
        <Row label="Status">
          <Tag type="gray" size="sm">
            Not Started
          </Tag>
        </Row>
        <Row label="Slot Months">
          <DrainTag drained={false} />
        </Row>
        <Row label="Raw Response">
          <DrainTag drained={false} />
        </Row>
        <Row label="Cashflows by Slot">
          <Tag type="gray" size="sm">
            No
          </Tag>
        </Row>
        <Row label="Cashflows by Month">
          <Tag type="gray" size="sm">
            No
          </Tag>
        </Row>
      </div>
    ) : (
      <div className={styles.detailGrid}>
        <Row label="Slot Months">
          <DrainTag drained={blob.slotMonthsDrained} />
        </Row>
        {blob.slotMonthsBlobPath && (
          <Row label="Slot Months Path">
            <BlobPath path={blob.slotMonthsBlobPath} />
          </Row>
        )}
        {blob.slotMonthsRowCount !== null &&
          blob.slotMonthsRowCount !== undefined && (
            <Row label="Slot Months Rows">
              {formatNumber(blob.slotMonthsRowCount, 0, 0, 'decimal', true)}
            </Row>
          )}
        {blob.slotMonthsDrainedAt && (
          <Row label="Slot Months Drained At">
            {new Date(blob.slotMonthsDrainedAt).toLocaleString()}
          </Row>
        )}
        <Row label="Raw Response">
          <DrainTag drained={blob.rawResponseDrained} />
        </Row>
        {blob.rawResponseBlobPath && (
          <Row label="Raw Response Path">
            <BlobPath path={blob.rawResponseBlobPath} />
          </Row>
        )}
        {blob.rawResponseDrainedAt && (
          <Row label="Raw Response Drained At">
            {new Date(blob.rawResponseDrainedAt).toLocaleString()}
          </Row>
        )}
        <Row label="Cashflows by Slot">
          <Tag type={blob.hasCashflowsBySlotBlob ? 'green' : 'gray'} size="sm">
            {blob.hasCashflowsBySlotBlob ? 'Yes' : 'No'}
          </Tag>
        </Row>
        {blob.cashflowsBySlotBlobPath && (
          <Row label="Cashflows by Slot Path">
            <BlobPath path={blob.cashflowsBySlotBlobPath} />
          </Row>
        )}
        <Row label="Cashflows by Month">
          <Tag type={blob.hasCashflowsByMonth ? 'green' : 'gray'} size="sm">
            {blob.hasCashflowsByMonth ? 'Pre-computed' : 'No'}
          </Tag>
        </Row>
        {blob.totalRoyaltyUndiscountedNetRevenue !== null && (
          <Row label="Total Royalty Net Revenue">
            {formatNumber(
              blob.totalRoyaltyUndiscountedNetRevenue,
              2,
              2,
              'currency'
            )}
          </Row>
        )}
      </div>
    )}
  </div>
);

const PackagesSection: FC<{ packages: PackageRef[] }> = ({ packages }) => (
  <div className={styles.section}>
    <h4>Package Evaluations ({packages.length})</h4>
    {packages.length === 0 ? (
      <p className={styles.emptyState}>
        Not referenced by any package evaluation
      </p>
    ) : (
      <div className={styles.detailGrid}>
        {packages.map((pkg) => (
          <Row
            key={pkg.packageEvaluationId}
            label={`Pkg Eval #${pkg.packageEvaluationId}`}
          >
            <a
              className={styles.link}
              href={`/packages/${pkg.packageId}/evaluations/${pkg.packageEvaluationId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {pkg.packageName ?? `Package #${pkg.packageId}`}
            </a>
          </Row>
        ))}
      </div>
    )}
  </div>
);

export const EvaluationOutputPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const [, setId] = useAtom(evaluationOutputIdAtom);
  const [loadable] = useAtom(evaluationOutputAtom);

  useEffect(() => {
    setId(id ? Number(id) : null);
  }, [id, setId]);

  if (loadable.state === 'loading') {
    return (
      <>
        <PageHeader
          breadcrumbs={[]}
          title="DA Evaluation Output"
          actions={null}
        />
        <div className={styles.container}>Loading...</div>
      </>
    );
  }

  if (loadable.state === 'hasError') {
    return (
      <>
        <PageHeader
          breadcrumbs={[]}
          title="DA Evaluation Output"
          actions={null}
        />
        <InlineNotification
          kind="error"
          title="Failed to load"
          subtitle={String(loadable.error)}
        />
      </>
    );
  }

  if (!loadable.data) {
    return (
      <>
        <PageHeader
          breadcrumbs={[]}
          title="DA Evaluation Output"
          actions={null}
        />
        <div className={styles.container}>No data</div>
      </>
    );
  }

  const { output, developmentArea, blob, packages } = loadable.data;

  return (
    <>
      <PageHeader
        breadcrumbs={[[routes.adminBlobDrainStatusPath, 'Blob Drain Status']]}
        title={`Evaluation Output #${output.id}`}
        actions={null}
      />
      <div className={styles.container}>
        {output.version === 1 && (
          <InlineNotification
            kind="warning"
            title="V1 Evaluation"
            subtitle="Package pages referencing this eval may time out due to slow v1 pricing views (pre-existing issue, not related to blob drain)."
            lowContrast
            hideCloseButton
          />
        )}
        <div className={styles.section}>
          <h4>Evaluation Output</h4>
          <div className={styles.detailGrid}>
            <Row label="ID">{output.id}</Row>
            <Row label="Version">
              <Tag type="blue" size="sm">
                v{output.version}
              </Tag>
            </Row>
            <Row label="Created">
              {new Date(output.createdAt).toLocaleString()}
            </Row>
            <Row label="Updated">
              {new Date(output.updatedAt).toLocaleString()}
            </Row>
            <Row label="Evaluation ID">{output.evaluationId ?? '-'}</Row>
            <Row label="Raw Response">
              <Tag type={output.hasRawResponse ? 'green' : 'gray'} size="sm">
                {output.hasRawResponse ? 'Present' : 'Nil'}
              </Tag>
            </Row>
            <Row label="Volumes by Month JSON">
              <Tag
                type={output.hasVolumesByMonthJson ? 'green' : 'gray'}
                size="sm"
              >
                {output.hasVolumesByMonthJson ? 'Attached' : 'No'}
              </Tag>
            </Row>
            <Row label="Slots">
              {output.slotsCount !== null && output.slotsCount !== undefined
                ? formatNumber(output.slotsCount, 0, 0, 'decimal', true)
                : '-'}
            </Row>
          </div>
        </div>

        {developmentArea ? (
          <DevelopmentAreaSection da={developmentArea} />
        ) : (
          <div className={styles.section}>
            <h4>Development Area</h4>
            <p className={styles.emptyState}>No evaluation linked</p>
          </div>
        )}

        <BlobSection blob={blob} />

        <PackagesSection packages={packages} />

        <div className={styles.section}>
          <h4>Links</h4>
          <div className={styles.detailGrid}>
            <Row label="Blob Drain Status">
              <a
                className={styles.link}
                href={routes.adminBlobDrainStatusPath}
                target="_blank"
                rel="noopener noreferrer"
              >
                Back to drain status
              </a>
            </Row>
          </div>
        </div>
      </div>
    </>
  );
};
